import * as React from "react";
const { useEffect, useState, useRef } = React;

import SBProvider from "@sendbird/uikit-react/SendbirdProvider";

// DirectDental brand mapping for SendBird UIKit. Primary drives outgoing
// bubbles, buttons, and unread accents; secondary is reserved for
// presence/positive accents (matches --green in directdental.scss).
const DD_COLOR_SET = {
  "--sendbird-light-primary-500": "#0a3a6e",
  "--sendbird-light-primary-400": "#0d4276",
  "--sendbird-light-primary-300": "#0f4b89",
  "--sendbird-light-primary-200": "#4c7fb3",
  "--sendbird-light-primary-100": "#e7eef6",
  "--sendbird-light-secondary-500": "#4c7406",
  "--sendbird-light-secondary-400": "#5e9007",
  "--sendbird-light-secondary-300": "#71ad08",
  "--sendbird-light-secondary-200": "#9cc356",
  "--sendbird-light-secondary-100": "#f1f7e6",
};
import "@sendbird/uikit-react/dist/index.css";
import "../../../../stylesheets/sendbird_overrides.scss";

import SBChatModal from "./SBChatModal";
import SBChatToastNotification from "./SBChatToast";

import { useStore } from "@nanostores/react";
import { SBChatShow, SBChatUnread, SBChatChannel, SBChatToast } from "../stores/SBChatStore";

import {
  BrowserView,
  MobileView,
  isBrowser,
  isMobile,
} from "react-device-detect";

import { createConsumer } from "@rails/actioncable";
import axios from "axios";

// Helper function to clean potentially malformed URLs
const cleanProfileUrl = (url) => {
  if (!url) return '/images/missing.svg';

  // Fix URLs with missing colon after protocol
  if (url.startsWith('https//') || url.startsWith('http//')) {
    return url.replace(/^(https?)\/{2}/, '$1://');
  }

  // Fix URLs with double protocol
  if (url.match(/^https?:\/\/https?:\/\//)) {
    return url.replace(/^https?:\/\//, '');
  }

  // Fix URLs with mixed protocol format
  if (url.match(/^https?:https?:\/\//)) {
    return url.replace(/^https?:/, '');
  }

  return url;
};

// Type definition for Sendbird provider instance
interface SendbirdInstance {
  connect?: () => Promise<any>;
  loadAllChannels?: () => Promise<any[]>;
  disconnect?: () => void;
  [key: string]: any; // Allow any other properties
}

export default function SBChat({
  avatar_url,
  nickname,
  profile_uuid,
  profile_id,
  app_id,
  activeOnMobile,
  isProfessional,
  assistantEnabled = false,
  userName = "",
}) {
  // Use a fallback value if app_id is missing or empty
  const appId = app_id || "MISSING_APP_ID";

  const unread = useStore(SBChatUnread);
  const show = useStore(SBChatShow);
  const channel = useStore(SBChatChannel);
  // The SendBird provider mounts the first time the tab is visible and then
  // stays mounted. It used to unmount on every tab switch, which tore the SDK
  // down mid-request and threw "Connection is required", "Store is not
  // initialized" and AbortError from inside the UIKit (Sentry DIRECTDENTAL-1ZM,
  // -1ZP, -1ZY, -296, -1ZK, -1ZN, -1ZZ, -1ZX, -207). The SDK already moves its
  // socket to the background state while the tab is hidden.
  const [providerReady, setProviderReady] = useState(!document.hidden);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const providerRef = useRef<SendbirdInstance | null>(null);
  const actionCableRef = useRef(null);
  const [csrfToken, setCsrfToken] = useState<string | null>(null);

  // Get CSRF token for API requests
  useEffect(() => {
    const token = document.querySelector<HTMLMetaElement>(
      'meta[name="csrf-token"]'
    )?.content;
    setCsrfToken(token || null);
  }, []);

  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get('chatopen') === '1') {
      SBChatShow.set(true);
    }
  }, []); // Empty dependency array ensures this runs only once

  // Mark messages as read when chat is shown
  useEffect(() => {
    if (show && csrfToken) {
      markMessagesAsRead();
    }
  }, [show, csrfToken]);

  const markMessagesAsRead = async () => {
    if (!csrfToken) {
      console.error('Cannot mark messages as read: CSRF token is missing');
      return;
    }

    try {
      const response = await axios.post('/sendbird/mark_as_read', {}, {
        headers: {
          'X-CSRF-Token': csrfToken
        }
      });

      if (response.status === 200) {
        // Update local store immediately
        SBChatUnread.set(0);
      }
    } catch (error) {
      console.error('Failed to mark messages as read:', error);
      // Don't update the unread count if the API call fails
    }
  };

  useEffect(() => {
    const handleResize = () => {
      const newIsMobile = window.innerWidth <= 768;
      setIsMobile(newIsMobile);
    };
    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  // Initialize SendBird connection and ActionCable subscription
  useEffect(() => {
    let isMounted = true;

    const initializeSendbird = async () => {
      try {
        // Create a SendBird connection if not already created
        if (!providerRef.current) {
          // Set a placeholder in providerRef so we don't keep trying to initialize
          providerRef.current = { initialized: true };
        }

        // Reset unread count initially
        if (isMounted) {
          SBChatUnread.set(0);
        }
      } catch (error) {
        console.error("SendBird initialization error:", error);
      }
    };

    // Initialize connection
    initializeSendbird();

    // Set up ActionCable subscription instead of polling
    try {
      const consumer = createConsumer();

      actionCableRef.current = consumer.subscriptions.create(
        { channel: "SendbirdNotificationsChannel" },
        {
          connected() {
            // Connection established
          },
          disconnected() {
            // Connection lost
          },
          rejected() {
            console.error("SendbirdNotificationsChannel subscription rejected");
          },
          received(data) {
            if (data.action === 'unread_update' && isMounted) {
              SBChatUnread.set(data.unread_count);

              // Only show toast notification if chat window is not already open
              // and we have all required data
              const shouldShowToast = data.toast &&
                data.message_content &&
                data.sender_nickname &&
                !show;

              if (shouldShowToast) {
                try {
                  // Validate channel URL before setting it
                  const channelUrl = data.channel_url || '';

                  // Check current toast state to avoid unnecessary updates
                  const currentToast = SBChatToast.get();
                  if (!currentToast.show) {
                    // Safely update the toast notification with timeout to prevent UI lockup
                    setTimeout(() => {
                      if (isMounted) {
                        SBChatToast.set({
                          show: true,
                          message: data.message_content,
                          sender: data.sender_nickname,
                          channelUrl: channelUrl
                        });
                      }
                    }, 0);
                  }
                } catch (error) {
                  console.error("[SBChat] Error showing toast notification:", error);
                  // Don't set the toast if there's an error
                }
              }
            }
          }
        }
      );
    } catch (error) {
      console.error("[SBChat] Failed to create ActionCable subscription:", error);
      // No fallback needed as updateUnreadCount is being removed
    }

    // Handle visibility changes
    const handleVisibilityChange = () => {
      const isNowActive = !document.hidden;
      if (isNowActive) setProviderReady(true);

      // If the tab becomes visible and chat is open, mark messages as read
      if (isNowActive && show && csrfToken) {
        markMessagesAsRead();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    const handleBeforeUnload = () => {
      // Unsubscribe from ActionCable
      if (actionCableRef.current) {
        actionCableRef.current.unsubscribe();
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      isMounted = false;
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      window.removeEventListener("beforeunload", handleBeforeUnload);

      // Unsubscribe from ActionCable
      if (actionCableRef.current) {
        actionCableRef.current.unsubscribe();
      }
    };
  }, [appId, profile_uuid, nickname, show, csrfToken]); // Added show and csrfToken dependencies

  const setShow = () => {
    SBChatShow.set(true);
    // No need to mark messages as read here anymore as the effect will handle it
  };

  return (
    <>
      {/* Chat Bubble Button with red dot indicator for unread messages */}
      <BrowserView>
        <a
          onClick={setShow}
          className="nav-link"
          data-tour="nav-chat"
          style={{ color: "#085ec8", position: "relative", cursor: "pointer" }}
        >
          {/* Chat Bubble Icon - FontAwesome */}
          <i className="fas fa-comment-alt" style={{ fontSize: 15 }}></i>
          &nbsp;Chat
          {/* Red dot indicator - Displayed if unread messages exist */}
          {unread > 0 && (
            <span
              style={{
                position: "absolute",
                top: "10px",
                left: "25px",
                background: "red",
                borderRadius: "50%",
                width: "8px",
                height: "8px",
                zIndex: 1,
              }}
            />
          )}
        </a>
      </BrowserView>
      <MobileView>
        <a
          onClick={setShow}
          className="nav-link d-inline-block"
          data-tour="nav-chat"
          style={{
            color: "#085ec8",
            position: "relative",
            width: "28px",
            height: "28px",
          }}
        >
          {/* Chat Bubble Icon - FontAwesome */}
          <i
            className="fas fa-comment-alt"
            style={{
              fontSize: 15,
              position: "absolute",
              left: "50%",
              top: "50%",
              transform: "translate(-50%, -50%)",
            }}
          ></i>
          {/* Red dot indicator - Displayed if unread messages exist */}
          {unread > 0 && (
            <span
              style={{
                position: "absolute",
                top: "40%",
                left: "60%",
                background: "red",
                borderRadius: "50%",
                width: "8px",
                height: "8px",
                zIndex: 1,
              }}
            />
          )}
        </a>
      </MobileView>

      {((activeOnMobile && isMobile) ||
        (!activeOnMobile && providerReady && !isMobile)) && (
          <>
            <SBProvider
              appId={appId}
              userId={profile_uuid}
              nickname={nickname}
              profileUrl={cleanProfileUrl(avatar_url)}
              colorSet={DD_COLOR_SET}
            >
              <SBChatModal
                avatar_url={avatar_url}
                nickname={nickname}
                profile_uuid={profile_uuid}
                profile_id={profile_id}
                app_id={appId}
                initialChannelUrl={channel ? channel : null}
                isProfessional={isProfessional}
                assistantEnabled={assistantEnabled}
                userName={userName}
              />
            </SBProvider>
          </>
        )}

      <SBChatToastNotification />
    </>
  );
}